package dev.kuml.portal.web.profile

import dev.kuml.portal.web.AppScope
import dev.kuml.portal.web.api.ApiResult
import dev.kuml.portal.web.api.OAuthConnectionResponse
import dev.kuml.portal.web.api.PortalApi
import dev.kuml.portal.web.components.AvatarWidget
import dev.kuml.portal.web.components.avatarWidget
import dev.kuml.portal.web.i18n.t
import io.kvision.core.Container
import io.kvision.core.onClick
import io.kvision.form.check.CheckBoxInput
import io.kvision.form.check.checkBoxInput
import io.kvision.form.text.TextInput
import io.kvision.form.text.textInput
import io.kvision.html.button
import io.kvision.html.div
import io.kvision.html.h1
import io.kvision.html.h2
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.w3c.dom.HTMLInputElement
import org.w3c.files.File

/**
 * Account profile: editable displayName (R-23), read-only email, plus the R-16 avatar
 * upload/removal controls. Reachable via [dev.kuml.portal.web.routing.Router]'s `/profile` route,
 * linked from the header avatar (see [dev.kuml.portal.web.components.portalHeader]).
 *
 * @param onIdentityChanged called after a successful displayName save (R-23) so the header's own
 *   avatar/initials ([dev.kuml.portal.web.components.PortalHeader.refreshIdentity]) can pick up
 *   the new name without waiting for the next navigation -- the header builds its avatar once from
 *   `GET /api/auth/me` at mount and otherwise never refreshes it.
 * @param initialOAuthCode R-5: the `?oauth=<code>` query parameter a `/link` callback redirect
 *   carried, resolved by [dev.kuml.portal.web.PortalApp] -- see [pendingOAuthCode][dev.kuml.portal.web.PortalApp]'s
 *   KDoc. `null` shows nothing.
 */
public fun Container.profileScreen(
    api: PortalApi,
    onIdentityChanged: () -> Unit = {},
    initialOAuthCode: String? = null,
) {
    div(className = "kportal-page kportal-prose") {
        div(className = "kportal-card") {
            h1(t.profile.pageTitle)

            var avatarHandle: AvatarWidget? = null
            val avatarRow = div(className = "kportal-profile-avatar-row")
            // R-23: the avatar's rendered initials are derived from the displayName CAPTURED WHEN
            // `avatarWidget(...)` was built (see `Avatar.kt` -- `AvatarWidget.refresh` only varies
            // the cache-buster, never the name). After a rename the widget must therefore be
            // REBUILT, not refreshed -- into this dedicated slot div, never via
            // `avatarRow.removeAll()`, which would also rip out the hidden file input attached
            // directly to avatarRow's own DOM node below.
            val avatarSlot = avatarRow.div()
            val emailDiv = div("", className = "kportal-card-meta")
            var currentDisplayName = ""

            // Hidden native file input -- KVision has no dedicated file-picker widget in this
            // codebase, so it is created directly via document.createElement and attached to
            // avatarRow's own DOM node (never innerHTML). The visible "Bild ändern" button below
            // just forwards its click to this hidden input.
            val fileInput = document.createElement("input") as HTMLInputElement
            fileInput.type = "file"
            fileInput.accept = "image/png,image/jpeg,image/webp"
            fileInput.style.display = "none"
            avatarRow.getElement()?.appendChild(fileInput)

            // R-23: editierbarer Anzeigename -- ausschließlich Kind-Widgets in `nameField`, kein
            // `content =` auf demselben Container gemischt (Projektkonvention).
            val nameField = div(className = "kportal-field")
            nameField.div(t.profile.displayNameLabel, className = "kportal-label")
            val nameInput = nameField.textInput(className = "kportal-input")
            nameInput.setAttribute("maxlength", DisplayNameInput.MAX_CHARACTERS.toString())
            nameInput.setAttribute("aria-label", t.profile.displayNameAriaLabel)
            val nameError = nameField.div("", className = "kportal-field-error")
            val saveNameButton =
                nameField.button(t.profile.saveName, className = "kportal-btn kportal-btn-primary kportal-btn-auto")
            saveNameButton.disabled = true

            val statusDiv = div(className = "kportal-status-area") { setAttribute("aria-live", "polite") }

            val actionsDiv = div(className = "kportal-field")
            val changeButton =
                actionsDiv.button(t.profile.changeImage, className = "kportal-btn kportal-btn-primary kportal-btn-auto")
            val removeButton =
                actionsDiv.button(t.profile.removeImage, className = "kportal-btn kportal-btn-text-neutral")
            removeButton.visible = false

            // R-11: Datennutzung -- zwei unabhaengige Consent-Schalter.
            h2(t.profile.dataUsageHeading)

            // Schalter 1: Opt-out (Widerspruch nach Art. 21 DSGVO) -- als POSITIVE Checkbox
            // dargestellt ("angehakt = erlaubt"), weil das die einzige Form ist, die Nutzer
            // verlaesslich richtig lesen. checked == !trainingOptOut.
            val trainingRow = div(className = "kportal-consent-row")
            val trainingCheck: CheckBoxInput = trainingRow.checkBoxInput(value = true)
            // Disabled until the real account state has been loaded (see [applyConsent] /
            // [reload] below) -- otherwise a user could click a checkbox that shows a fabricated
            // constructor default (see comment on [suppressChangeEvents]) and get a silent,
            // unpersisted no-op with a misleading visual "success" (the DOM checkbox still
            // toggles even though the observer bails out).
            trainingCheck.disabled = true
            trainingRow.div(className = "kportal-consent-text") {
                div(t.profile.trainingConsentLabel, className = "kportal-label")
                div(t.profile.trainingConsentHint, className = "kportal-consent-hint")
            }

            // Schalter 2: getrennte, freiwillige Einwilligung (Art. 6 Abs. 1 lit. a DSGVO).
            val showcaseRow = div(className = "kportal-consent-row")
            val showcaseCheck: CheckBoxInput = showcaseRow.checkBoxInput(value = false)
            // Same reasoning as [trainingCheck.disabled] above.
            showcaseCheck.disabled = true
            showcaseRow.div(className = "kportal-consent-text") {
                div(t.profile.showcaseConsentLabel, className = "kportal-label")
                div(t.profile.showcaseConsentHint, className = "kportal-consent-hint")
            }

            // R-5: verknuepfte Social-Login-Konten. Hidden entirely (hostVisible = false, never
            // just empty) until the async provider-list fetch resolves to a NON-empty list -- see
            // [loadOAuthConnections] below. Rendered AFTER the consent block, BEFORE statusDiv is
            // read below (setStatus is shared with the OAuth notice).
            val oauthHeading = h2(t.profile.connectedAccountsTitle)
            oauthHeading.visible = false
            val oauthIntro = div(t.profile.connectedAccountsIntro, className = "kportal-card-meta")
            oauthIntro.visible = false
            val oauthRowsHost = div(className = "kportal-profile-oauth-rows")
            oauthRowsHost.visible = false

            fun setStatus(
                message: String,
                isError: Boolean,
            ) {
                statusDiv.content = message
                if (isError) statusDiv.addCssClass("kportal-status-error") else statusDiv.removeCssClass("kportal-status-error")
            }

            // Guards the two checkboxes' `subscribe` observers while a value is being set
            // PROGRAMMATICALLY (the observer fires on EVERY `.value =` assignment, not just
            // user-driven ones -- see `CheckInput.subscribe`'s KDoc) so that assignment never
            // itself triggers another PATCH request. Starts `true`: `MutableState.subscribe`
            // invokes the observer once, synchronously, with the widget's current (constructor
            // default) value at registration time, before `reload()`'s first real `applyConsent`
            // call has run -- that synthetic first call must be suppressed too.
            var suppressChangeEvents = true

            fun applyConsent(
                trainingOptOut: Boolean,
                showcaseOptIn: Boolean,
            ) {
                suppressChangeEvents = true
                trainingCheck.value = !trainingOptOut
                showcaseCheck.value = showcaseOptIn
                suppressChangeEvents = false
                // Only ever reachable with real, server-confirmed consent state (initial
                // successful `reload()` via `applyUser`, or a successful `updateConsent` PATCH
                // response) -- safe to allow interaction from here on.
                trainingCheck.disabled = false
                showcaseCheck.disabled = false
            }

            fun applyUser(
                userId: String,
                displayName: String,
                email: String,
                avatarUpdatedAt: String?,
                trainingOptOut: Boolean,
                showcaseOptIn: Boolean,
                rebuildAvatar: Boolean = false,
            ) {
                currentDisplayName = displayName
                nameInput.value = displayName
                saveNameButton.disabled = true
                nameError.content = ""
                emailDiv.content = email
                val existing = avatarHandle
                if (existing == null) {
                    avatarHandle = avatarSlot.avatarWidget(userId, displayName, avatarUpdatedAt, "kportal-avatar-lg")
                } else if (rebuildAvatar) {
                    // See `avatarSlot`'s KDoc above -- a rename changes the initials fallback,
                    // which `AvatarWidget.refresh` cannot pick up on its own.
                    avatarSlot.removeAll()
                    avatarHandle = avatarSlot.avatarWidget(userId, displayName, avatarUpdatedAt, "kportal-avatar-lg")
                } else {
                    existing.refresh(avatarUpdatedAt)
                }
                removeButton.visible = avatarUpdatedAt != null
                applyConsent(trainingOptOut, showcaseOptIn)
            }

            fun reload() {
                AppScope.launch {
                    when (val result = api.me()) {
                        is ApiResult.Success -> {
                            val user = result.value
                            applyUser(user.id, user.displayName, user.email, user.avatarUpdatedAt, user.trainingOptOut, user.showcaseOptIn)
                        }
                        else -> {
                            // No confirmed consent state available (yet) -- keep both switches
                            // disabled rather than let them display an unconfirmed/stale value
                            // that a click could not actually persist. See [suppressChangeEvents].
                            trainingCheck.disabled = true
                            showcaseCheck.disabled = true
                            setStatus(t.profile.profileLoadFailed, true)
                        }
                    }
                }
            }

            // R-23: saves the edited displayName. Uses the PATCH response directly (which already
            // carries the full, server-normalized `UserResponse`) instead of a follow-up
            // `reload()` -- exactly the same "one round trip, not two" discipline as the consent
            // toggles above.
            fun saveDisplayName() {
                val candidate = DisplayNameInput.normalize(nameInput.value.orEmpty())
                if (!DisplayNameInput.isSubmittable(candidate) || !DisplayNameInput.isChanged(candidate, currentDisplayName)) return
                saveNameButton.disabled = true
                AppScope.launch {
                    setStatus(t.profile.savingName, false)
                    when (val result = api.updateProfile(candidate)) {
                        is ApiResult.Success -> {
                            setStatus(t.profile.nameSaved, false)
                            val user = result.value
                            applyUser(
                                user.id,
                                user.displayName,
                                user.email,
                                user.avatarUpdatedAt,
                                user.trainingOptOut,
                                user.showcaseOptIn,
                                rebuildAvatar = true,
                            )
                            onIdentityChanged()
                        }
                        is ApiResult.HttpError -> {
                            // R-18: the server no longer supplies a rendered message (decision E-9)
                            // -- resolve via the `code` it now sends instead (see
                            // `AccountRoutes.kt`'s `display_name_*` codes, this wave's plan §7.3).
                            val message = t.errors.forCode(result.error?.code, result.status) ?: t.profile.nameSaveFailed
                            nameError.content = message
                            setStatus(t.profile.nameSaveFailed, true)
                            saveNameButton.disabled = false
                        }
                        ApiResult.NetworkError -> {
                            setStatus(t.profile.networkSaveError, true)
                            saveNameButton.disabled = false
                        }
                    }
                }
            }

            nameInput.setEventListener<TextInput> {
                input = {
                    val raw = nameInput.value.orEmpty()
                    val submittable = DisplayNameInput.isSubmittable(raw)
                    val changed = DisplayNameInput.isChanged(raw, currentDisplayName)
                    saveNameButton.disabled = !submittable || !changed
                    nameError.content = ""
                }
                keydown = { e ->
                    if (e.key == "Enter") {
                        e.preventDefault()
                        saveDisplayName()
                    }
                }
            }
            saveNameButton.onClick { saveDisplayName() }

            // `subscribe` (KVision's `MutableState<Boolean>` API, not a raw DOM `change`
            // listener) fires on EVERY `.value` assignment -- both real user clicks (via
            // `CheckInput`'s own internal click/change listener updating `.value` from the DOM)
            // and programmatic ones (`applyConsent` above) -- hence the [suppressChangeEvents]
            // guard around every programmatic assignment in this file.
            trainingCheck.subscribe { checked ->
                if (suppressChangeEvents) return@subscribe
                AppScope.launch {
                    when (val result = api.updateConsent(trainingOptOut = !checked, showcaseOptIn = null)) {
                        is ApiResult.Success -> {
                            setStatus(t.profile.settingSaved, false)
                            applyConsent(result.value.trainingOptOut, result.value.showcaseOptIn)
                        }
                        is ApiResult.HttpError -> {
                            setStatus(t.errors.forCode(result.error?.code, result.status) ?: t.profile.settingSaveFailed, true)
                            suppressChangeEvents = true
                            trainingCheck.value = !checked
                            suppressChangeEvents = false
                        }
                        ApiResult.NetworkError -> {
                            setStatus(t.profile.networkSaveError, true)
                            suppressChangeEvents = true
                            trainingCheck.value = !checked
                            suppressChangeEvents = false
                        }
                    }
                }
            }

            showcaseCheck.subscribe { checked ->
                if (suppressChangeEvents) return@subscribe
                AppScope.launch {
                    when (val result = api.updateConsent(trainingOptOut = null, showcaseOptIn = checked)) {
                        is ApiResult.Success -> {
                            setStatus(t.profile.settingSaved, false)
                            applyConsent(result.value.trainingOptOut, result.value.showcaseOptIn)
                        }
                        is ApiResult.HttpError -> {
                            setStatus(t.errors.forCode(result.error?.code, result.status) ?: t.profile.settingSaveFailed, true)
                            suppressChangeEvents = true
                            showcaseCheck.value = !checked
                            suppressChangeEvents = false
                        }
                        ApiResult.NetworkError -> {
                            setStatus(t.profile.networkSaveError, true)
                            suppressChangeEvents = true
                            showcaseCheck.value = !checked
                            suppressChangeEvents = false
                        }
                    }
                }
            }

            changeButton.onClick { fileInput.click() }

            fileInput.addEventListener(
                "change",
                {
                    val file: File? = fileInput.files?.item(0)
                    fileInput.value = "" // allow re-selecting the same file later
                    if (file != null) {
                        AppScope.launch {
                            setStatus(t.profile.uploadingImage, false)
                            val toUpload = encodeAvatar(file) ?: file
                            when (val result = api.uploadAvatar(toUpload)) {
                                is ApiResult.Success -> {
                                    setStatus(t.profile.avatarUpdated, false)
                                    reload()
                                }
                                is ApiResult.HttpError ->
                                    setStatus(t.errors.forCode(result.error?.code, result.status) ?: t.profile.avatarUploadFailed, true)
                                ApiResult.NetworkError -> setStatus(t.profile.networkSaveError, true)
                            }
                        }
                    }
                },
            )

            removeButton.onClick {
                AppScope.launch {
                    when (val result = api.deleteAvatar()) {
                        is ApiResult.Success -> {
                            setStatus(t.profile.avatarRemoved, false)
                            reload()
                        }
                        is ApiResult.HttpError ->
                            setStatus(t.errors.forCode(result.error?.code, result.status) ?: t.profile.avatarRemoveFailed, true)
                        ApiResult.NetworkError -> setStatus(t.profile.networkSaveError, true)
                    }
                }
            }

            // R-5: label per known provider id -- an id this client does not (yet) recognise is
            // silently skipped, exactly like `AuthScreen`'s equivalent `when` (never crashes on a
            // provider added server-side before the client is redeployed).
            fun providerLabel(providerId: String): String? =
                when (providerId) {
                    "google" -> t.auth.signInWithGoogle
                    "github" -> t.auth.signInWithGitHub
                    else -> null
                }

            // Takes an [onUnlinked] callback rather than calling `loadOAuthConnections()` directly
            // -- Kotlin local functions cannot forward-reference one another, and this keeps the
            // two functions independently orderable instead of needing a synthetic forward
            // declaration.
            fun renderOAuthRow(
                providerId: String,
                connection: OAuthConnectionResponse?,
                onUnlinked: () -> Unit,
            ) {
                val label = providerLabel(providerId) ?: return
                val row = oauthRowsHost.div(className = "kportal-profile-oauth-row")
                row.div(className = "kportal-profile-oauth-info") {
                    div(label, className = "kportal-label")
                    val statusLabel =
                        if (connection != null) {
                            t.profile.connectedSince(t.format.date(connection.createdAt) ?: connection.createdAt)
                        } else {
                            t.profile.notConnected
                        }
                    div(statusLabel, className = "kportal-card-meta")
                }
                if (connection != null) {
                    val disconnectButton = row.button(t.profile.disconnectAction, className = "kportal-btn kportal-btn-text-neutral")
                    disconnectButton.onClick {
                        AppScope.launch {
                            when (val result = api.unlinkOAuth(providerId)) {
                                is ApiResult.Success -> {
                                    setStatus(t.profile.settingSaved, false)
                                    onUnlinked()
                                }
                                is ApiResult.HttpError ->
                                    setStatus(
                                        if (result.error?.code == "oauth_last_login_method") {
                                            t.profile.lastLoginMethodRefused
                                        } else {
                                            t.errors.forCode(result.error?.code, result.status) ?: t.profile.settingSaveFailed
                                        },
                                        true,
                                    )
                                ApiResult.NetworkError -> setStatus(t.profile.networkSaveError, true)
                            }
                        }
                    }
                } else {
                    val connectButton = row.button(t.profile.connectAction, className = "kportal-btn kportal-btn-primary kportal-btn-auto")
                    connectButton.onClick { window.location.assign("/api/auth/oauth/$providerId/link") }
                }
            }

            fun loadOAuthConnections() {
                AppScope.launch {
                    when (val providersResult = api.oauthProviders()) {
                        is ApiResult.Success -> {
                            val configured = providersResult.value.providers
                            if (configured.isEmpty()) return@launch
                            val connections =
                                when (val connectionsResult = api.oauthConnections()) {
                                    is ApiResult.Success -> connectionsResult.value
                                    else -> emptyList()
                                }
                            val connectionsByProvider = connections.associateBy { it.provider }
                            oauthRowsHost.removeAll()
                            configured.forEach { providerId ->
                                renderOAuthRow(providerId, connectionsByProvider[providerId], onUnlinked = ::loadOAuthConnections)
                            }
                            oauthHeading.visible = true
                            oauthIntro.visible = true
                            oauthRowsHost.visible = true
                        }
                        else -> Unit // stays hidden, see oauthHeading/oauthIntro/oauthRowsHost's own comment above.
                    }
                }
            }

            // R-5: shows the notice from a `/link` callback redirect, if any -- see
            // [Container.profileScreen]'s own KDoc on [initialOAuthCode].
            when (initialOAuthCode) {
                null -> Unit
                "linked" -> setStatus(t.profile.linkedSuccess, false)
                "link_taken" -> setStatus(t.profile.linkTaken, true)
                "link_slot" -> setStatus(t.profile.linkSlotOccupied, true)
                "unavailable" -> setStatus(t.auth.oauthUnavailableNotice, true)
                else -> setStatus(t.auth.oauthGenericError, true)
            }

            loadOAuthConnections()
            reload()
        }
    }
}
